import { AlertTriangle, CheckCircle2, Clock, Rocket } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link } from "react-router";
import { Button } from "src/components/ui/button";
import { MarkdownEditorLazy as MarkdownEditor } from "src/components/ui/markdown-editor-lazy";
import type { GenerationStatusType } from "src/types/onboarding-settings";
import { getKnowledgeFileName } from "src/utils/brandTheme";

interface ReviewStepProps {
  generationStatus: GenerationStatusType;
  instructionContent?: string;
  onStartInitialization: () => void;
  onConfirm: (editedContent: string) => void;
  isStartingInitialization?: boolean;
  isLoading?: boolean;
  productName: string;
  brandTheme?: string | null;
  onCancel?: () => void;
}

export const ReviewStep: React.FC<ReviewStepProps> = ({
  generationStatus,
  instructionContent,
  onStartInitialization,
  onConfirm,
  isStartingInitialization,
  isLoading,
  productName,
  brandTheme,
  onCancel,
}) => {
  const [editedContent, setEditedContent] = useState(instructionContent || "");
  const knowledgeFileName = getKnowledgeFileName(brandTheme);

  useEffect(() => {
    if (instructionContent) {
      setEditedContent(instructionContent);
    }
  }, [instructionContent]);

  // Pending state - show button to start initialization
  if (generationStatus === "pending") {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">
            Ready to Initialize
          </h2>
          <p className="text-muted-foreground mt-2">
            {productName} has everything it needs to start learning your environment.
          </p>
        </div>

        <div className="bg-card/50 rounded-lg p-8 text-center">
          <Rocket className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground mb-6">
            When you start initialization, {productName} will index your integrations, generate memories
            about your environment, and bootstrap its own instruction file. This process takes about an hour.
          </p>
          <Button
            size="lg"
            onClick={onStartInitialization}
            disabled={isStartingInitialization}
          >
            {isStartingInitialization ? "Starting..." : "Start Initialization"}
          </Button>
        </div>

        {onCancel && (
          <div className="flex justify-start pt-4">
            <Button variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        )}
      </div>
    );
  }

  // In-progress state - show processing message
  if (generationStatus === "in_progress") {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">
            Initializing {productName}
          </h2>
        </div>

        <div className="bg-card/50 rounded-lg p-8 text-center">
          <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-lg text-foreground mb-2">
            {productName} is learning your environment.
          </p>
          <p className="text-muted-foreground">
            This process generally takes about an hour. Check back in to review your setup and finish onboarding.
          </p>
        </div>

        {onCancel && (
          <div className="flex justify-start pt-4">
            <Button variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        )}
      </div>
    );
  }

  if (generationStatus === "failed") {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">
            Setup Failed
          </h2>
        </div>
        <div className="text-center py-12">
          <p className="text-red-400">
            Something went wrong during initialization. Please contact support.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-foreground">
          Setup Complete
        </h2>
        <p className="text-muted-foreground mt-2">
          {productName} has finished learning about your environment. Here&apos;s what we set up:
        </p>
      </div>

      {/* Summary of what was done */}
      <div className="space-y-4">
        <div className="bg-card/50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-foreground font-medium">Indexed Resources</p>
              <p className="text-muted-foreground text-sm mt-1">
                {productName} pulled information from your integrations to power its research.{" "}
                <Link to="/context/resources" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80">
                  See Ingested Resources →
                </Link>
              </p>
            </div>
          </div>
        </div>

        <div className="bg-card/50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-foreground font-medium">Generated Memories</p>
              <p className="text-muted-foreground text-sm mt-1">
                {productName} learned about your organizational processes and how you debug issues.{" "}
                <Link to="/context/snippets" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80">
                  See Memory Explorer →
                </Link>
              </p>
            </div>
          </div>
        </div>

        <div className="bg-card/50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-foreground font-medium">Initialized {knowledgeFileName}</p>
              <p className="text-muted-foreground text-sm mt-1">
                {productName} created an instruction file to guide investigations. If you created a team,
                they get their own file for team-specific strategies too.
              </p>

              {/* Warning about experimental generation */}
              <div className="mt-4 bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <p className="text-amber-200 text-sm">
                    <span className="font-medium">Note:</span> {knowledgeFileName} generation may make mistakes.
                    Please review the content below to ensure accuracy. You can always edit this later.
                  </p>
                </div>
              </div>

              {/* Markdown editor inside the card */}
              <div className="mt-4">
                <MarkdownEditor
                  value={editedContent}
                  onChange={setEditedContent}
                  minHeight="300px"
                  height="400px"
                  placeholder={`${knowledgeFileName} content...`}
                  autoFocus={false}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between pt-4">
        {onCancel ? (
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        ) : <div />}
        <Button
          onClick={() => onConfirm(editedContent)}
          disabled={isLoading || !editedContent.trim()}
        >
          {isLoading ? "Saving..." : "Confirm & Finish"}
        </Button>
      </div>
    </div>
  );
};
